import { useEffect, useState } from "react";
import { Loader2, Check, ArrowRight } from "lucide-react";
import { getProgramCheckoutStatus } from "@/lib/checkout";

const LOGO =
  "https://customer-assets.emergentagent.com/job_data-driven-baseball/artifacts/94x88gnl_ChatGPT%20Image%20Jun%2026%2C%202026%2C%2003_59_37%20PM.png";

export default function TrainingSuccess() {
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const data = await getProgramCheckoutStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (err) {
        console.error("Program status poll failed:", err);
      }
      if (attempts >= 8) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center">
        <img src={LOGO} alt="Rep Lab" className="h-20 w-auto object-contain mix-blend-screen mx-auto mb-10" />

        {state === "checking" && (
          <div data-testid="training-success-checking">
            <Loader2 size={40} className="animate-spin text-[#2563EB] mx-auto" />
            <p className="mt-6 text-lg font-semibold text-white/70">Confirming your registration…</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="training-success-paid">
            <div className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#2563EB]/15 text-[#2563EB]">
              <Check size={32} strokeWidth={3} />
            </div>
            <h1 className="mt-8 text-4xl sm:text-5xl font-extrabold tracking-tighter uppercase text-white leading-[0.95]">
              You're in.
            </h1>
            <p className="mt-6 text-lg font-medium text-white/60 leading-relaxed">
              {info.athlete_name ? `${info.athlete_name} is ` : "Your athlete is "}registered for Rep Lab training.
            </p>
            {Array.isArray(info.programs) && info.programs.length > 0 && (
              <div className="mt-6 space-y-3 text-left inline-block">
                {info.programs.map((p, i) => {
                  const single = p.start_date && p.start_date === p.end_date;
                  const range = [p.time, p.end_time].filter(Boolean).join(" – ");
                  const sched = (single ? [range] : [p.day, range]).filter(Boolean).join(" · ") + (p.format === "lesson" && p.session_minutes ? ` (${p.session_minutes} min)` : "");
                  const nice = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { weekday: single ? "long" : "short", month: single ? "long" : "short", day: "numeric", timeZone: "UTC" });
                  const isIso = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d || "");
                  const dates = single
                    ? nice(p.start_date)
                    : [p.start_date, p.end_date].filter(Boolean).map((d) => (isIso(d) ? nice(d) : d)).join(" – ");
                  const detail = [p.age_group, dates, sched].filter(Boolean).join(" · ");
                  return (
                    <div key={i} className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4">
                      <p className="text-sm font-extrabold text-white flex items-center gap-2"><Check size={16} className="text-[#60a5fa]" /> {p.title}</p>
                      {detail && <p className="mt-1 text-xs font-medium text-white/50 pl-6">{detail}</p>}
                      {p.price != null && <p className="mt-1 text-xs font-semibold text-white/40 pl-6">${p.price}</p>}
                    </div>
                  );
                })}
                {info.amount != null && (
                  <div className="flex items-center justify-between gap-6 px-5 pt-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/40">Amount Paid</span>
                    <span data-testid="training-success-amount" className="text-lg font-extrabold text-white">${info.amount}</span>
                  </div>
                )}
              </div>
            )}
            <p className="mt-6 text-base font-medium text-white/40">Registration complete. Check your email for your confirmation and schedule details.</p>
            {info.booking_type === "dropin" && (
              <div data-testid="dropin-promo" className="mt-8 rounded-2xl border border-white/15 p-5 text-left">
                <p className="text-base font-bold text-white">Enjoyed Rep Lab? Join a four-week training group for $140 and save $20 compared with four individual drop-ins.</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <a data-testid="dropin-promo-programs" href={`/training/${info.programs?.[0]?.program_type || "hitting"}?format=four_week`} className="rounded-full bg-white text-[#0B0F19] px-5 py-2.5 text-sm font-bold">Browse four-week programs</a>
                  <a data-testid="dropin-promo-lessons" href="/lessons" className="rounded-full border border-white/25 text-white px-5 py-2.5 text-sm font-bold">Book a private lesson</a>
                </div>
              </div>
            )}
            <a href="/" data-testid="training-success-home-btn" className="mt-10 inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] text-white text-base font-bold px-8 py-4 rounded-full transition-colors">
              Back to Home <ArrowRight size={18} />
            </a>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="training-success-pending">
            <div className="mx-auto inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 text-white">
              <Check size={32} strokeWidth={3} />
            </div>
            <h1 className="mt-8 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Payment received.</h1>
            <p className="mt-6 text-lg font-medium text-white/60 leading-relaxed">We're finalizing your registration. You'll receive a confirmation email shortly.</p>
            <a href="/" className="mt-10 inline-flex items-center gap-2 text-[#2563EB] font-bold">Back to Home <ArrowRight size={18} /></a>
          </div>
        )}

        {state === "error" && (
          <div data-testid="training-success-error">
            <h1 className="text-3xl font-extrabold tracking-tight text-white">Something went wrong.</h1>
            <p className="mt-6 text-lg font-medium text-white/60">We couldn't confirm your session. If you were charged, email info@replabbaseball.com.</p>
            <a href="/" className="mt-10 inline-flex items-center gap-2 text-[#2563EB] font-bold">Back to Home <ArrowRight size={18} /></a>
          </div>
        )}
      </div>
    </div>
  );
}
